import * as React from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, Stack,
    Step, StepLabel, Stepper, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getAiWorkflowRun, getMatchBriefExecution } from '../../Api';
import { AiWorkflowRunDetailDto } from '../../models/dtos/AiDto';
import { MatchBriefExecutionDto, MatchBriefExecutionStatus } from '../../models/dtos/MatchBriefDto';
import AiWorkflowRunView from '../ai/AiWorkflowRunView';
import { formatAiDuration, formatAiUsd } from '../ai/AiShared';
import MatchBriefDialog from './MatchBriefDialog';

/** The hub workflow AI match briefs run in. */
export const MATCH_BRIEF_WORKFLOW = 'match.brief';
/** The one a player's on-demand prediction runs in (billed to them; betup-server-api docs/match-briefs.md §1). */
export const MATCH_BRIEF_ON_DEMAND_WORKFLOW = 'match.brief.on-demand';

/** The hub workflow an invocation's calls were made in. */
export const briefWorkflowOf = (source: string | null | undefined) =>
    source === 'ON_DEMAND' ? MATCH_BRIEF_ON_DEMAND_WORKFLOW : MATCH_BRIEF_WORKFLOW;

/** Who started an invocation: the scheduler, the dashboard, or a player asking Ace for the prediction. */
export const briefSourceLabel = (source: string | null | undefined) =>
    source === 'MANUAL' ? 'Manual' : source === 'ON_DEMAND' ? 'Player (on demand)' : 'Scheduler';

/** The working stages shown as steps (NOT_STARTED is "received", COMPLETED / FAILED the end). */
const BRIEF_STEPS: MatchBriefExecutionStatus[] = ['NOT_STARTED', 'MATCH_ASSIGNED', 'COLLECTING_CONTEXT', 'WEB_RESEARCH', 'SYNTHESIS', 'TRANSLATIONS', 'COMPLETED'];
/** A research-only invocation stops after the web research. */
const RESEARCH_STEPS: MatchBriefExecutionStatus[] = ['NOT_STARTED', 'MATCH_ASSIGNED', 'COLLECTING_CONTEXT', 'WEB_RESEARCH', 'COMPLETED'];

/** "Prediction" (the whole brief) or "Research only". */
export const SCOPE_LABELS: Record<string, string> = { BRIEF: 'Prediction', RESEARCH: 'Research only' };

export const STAGE_LABELS: Record<MatchBriefExecutionStatus, string> = {
    NOT_STARTED: 'Received',
    MATCH_ASSIGNED: 'Match assigned',
    COLLECTING_CONTEXT: 'Collecting context',
    WEB_RESEARCH: 'Web research',
    SYNTHESIS: 'Synthesis',
    TRANSLATIONS: 'Translations',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
};

export function MatchBriefStageChip({ status }: { status: MatchBriefExecutionStatus }) {
    const color = status === 'COMPLETED' ? 'success' : status === 'FAILED' ? 'error' : status === 'NOT_STARTED' ? 'default' : 'info';
    return <Chip size="small" color={color} variant={status === 'NOT_STARTED' ? 'outlined' : 'filled'} label={STAGE_LABELS[status] ?? status} />;
}

export const executionMatchTitle = (e: MatchBriefExecutionDto) =>
    e.match ? (e.match.home && e.match.away ? `${e.match.home} – ${e.match.away}` : `Match ${e.match.id}`) : null;

const time = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString() : '');

/**
 * One invocation: its stages with their times, why it failed or had nothing to do, the brief it wrote, and the AI hub
 * view of its correlation id — cost per task and agent, the research run — with a link to every call.
 */
export default function MatchBriefExecutionDialog({ executionId, onClose }: { executionId: number | null; onClose: () => void }) {
    const [execution, setExecution] = React.useState<MatchBriefExecutionDto | null>(null);
    const [run, setRun] = React.useState<AiWorkflowRunDetailDto | null>(null);
    const [runLoading, setRunLoading] = React.useState(false);
    const [briefOpen, setBriefOpen] = React.useState(false);

    React.useEffect(() => {
        setExecution(null);
        setRun(null);
        if (executionId == null) return;
        let cancelled = false;
        getMatchBriefExecution(executionId).then(result => {
            if (cancelled) return;
            setExecution(result);
            if (result?.correlation_id) {
                setRunLoading(true);
                getAiWorkflowRun(briefWorkflowOf(result.source), result.correlation_id).then(r => {
                    if (!cancelled) { setRun(r); setRunLoading(false); }
                });
            }
        });
        return () => { cancelled = true; };
    }, [executionId]);

    const STEPS = execution?.scope === 'RESEARCH' ? RESEARCH_STEPS : BRIEF_STEPS;
    const reached = (stage: MatchBriefExecutionStatus) => execution?.stages?.find(s => s.stage === stage)?.at;
    const failedAt = execution?.status === 'FAILED' ? execution.failed_stage : null;
    const activeStep = execution
        ? execution.status === 'FAILED' ? STEPS.indexOf(failedAt ?? 'NOT_STARTED') : STEPS.indexOf(execution.status) + (execution.status === 'COMPLETED' ? 1 : 0)
        : 0;
    const title = execution ? executionMatchTitle(execution) : null;

    return (
        <>
            <Dialog open={executionId != null} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <span>Invocation #{executionId}{title ? ` · ${title}` : ''}</span>
                        {execution && <MatchBriefStageChip status={execution.status} />}
                        {execution && <Chip size="small" variant="outlined" label={execution.source.toLowerCase()} />}
                        {execution && <Chip size="small" variant="outlined" color={execution.scope === 'RESEARCH' ? 'secondary' : 'default'} label={SCOPE_LABELS[execution.scope] ?? execution.scope} />}
                    </Stack>
                    {execution && (
                        <Typography variant="body2" color="text.secondary">
                            Received {new Date(execution.created_at).toLocaleString()}
                            {execution.requested_by ? ` · by ${execution.requested_by}` : ''}
                            {execution.requested_match_id ? ` · asked for match ${execution.requested_match_id}` : ' · match picked automatically'}
                            {execution.finished_at ? ` · took ${formatAiDuration(execution.created_at, execution.finished_at)}` : ''}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {!execution && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
                    {execution && (
                        <Stack gap={2.5}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {STEPS.map(stage => (
                                    <Step key={stage} completed={!!reached(stage) && stage !== failedAt && (execution.status === 'COMPLETED' || STEPS.indexOf(stage) < activeStep)}>
                                        <StepLabel error={stage === failedAt} optional={<Typography variant="caption">{time(reached(stage))}</Typography>}>
                                            {STAGE_LABELS[stage]}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            {execution.error && <Alert severity="error">Failed at {STAGE_LABELS[execution.failed_stage ?? 'NOT_STARTED']}: {execution.error}</Alert>}
                            {execution.note && <Alert severity="info">{execution.note}</Alert>}
                            <Stack direction="row" gap={3} flexWrap="wrap" alignItems="flex-end">
                                <Meta label="Match" value={execution.match
                                    ? <Link component={RouterLink} to={`/matches/${execution.match.id}`} onClick={onClose}>{title} ({execution.match.league ?? execution.match.id})</Link>
                                    : '—'} />
                                <Meta label="Kick-off" value={execution.match?.start ? new Date(execution.match.start).toLocaleString() : '—'} />
                                <Meta label="AI cost" value={formatAiUsd(execution.cost_usd)} />
                                {execution.research_id && (
                                    <Meta label={execution.research_reused ? 'Research (reused)' : 'Research'} value={execution.research_run_id
                                        ? <Link component={RouterLink} to={`/ai/runs/${execution.research_run_id}`} onClick={onClose}>#{execution.research_id}, {execution.researched_at ? new Date(execution.researched_at).toLocaleString() : '—'}</Link>
                                        : `#${execution.research_id}`} />
                                )}
                                <Meta label="Correlation id" value={<span style={{ fontFamily: 'monospace' }}>{execution.correlation_id ?? '—'}</span>} />
                                {execution.brief_id && <Button size="small" variant="outlined" onClick={() => setBriefOpen(true)}>Open brief</Button>}
                                {execution.correlation_id && (
                                    <Button size="small" component={RouterLink} onClick={onClose}
                                        to={`/ai/workflows/${briefWorkflowOf(execution.source)}/runs/${encodeURIComponent(execution.correlation_id)}`}>
                                        All AI calls
                                    </Button>
                                )}
                            </Stack>
                            {execution.correlation_id && (
                                <>
                                    <Divider />
                                    <Typography variant="subtitle1">AI workflow run</Typography>
                                    {runLoading && <CircularProgress size={24} />}
                                    {!runLoading && !run && <Typography variant="body2" color="text.secondary">The AI hub has no calls for this correlation id yet.</Typography>}
                                    {run && <AiWorkflowRunView run={run} compact onNavigate={onClose} />}
                                </>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
            </Dialog>
            <MatchBriefDialog briefId={briefOpen && execution?.brief_id ? execution.brief_id : null} onClose={() => setBriefOpen(false)} />
        </>
    );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2" component="div">{value}</Typography>
        </Box>
    );
}
